import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components/macro";
import { resultStateEnums } from "../../../common/failureEnums";
import { useHistory } from "react-router-dom";
import { dataSource, dataSourceReport } from "../../../common/paths";
import useDataSourceNotifications from "../../../Hooks/useDataSourceNotifications";

const ReportStatusContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  z-index: 10;
`;

const ReportStatus = styled.div`
  background: rgb(0, 153, 255);
  padding: 0.8rem 1.5rem;
  color: #efefef;
  border-radius: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.16), 0 2px 4px rgba(0, 0, 0, 0.23);
`;

const ReportProgressStatus = ({
  sourceId,
  reportId,
  fetchLatestReport,
  isGeneratingCachedReport,
}) => {
  const { lastDataSourceNotification, setLastDataSourceNotification } =
    useDataSourceNotifications();

  const [progressBar, setProgressBar] = useState({
    showProgress: isGeneratingCachedReport,
    inProgress: isGeneratingCachedReport,
    failed: "",
  });

  const lastHandledNotificationRef = useRef();
  const history = useHistory();

  useEffect(() => {
    const payload = lastDataSourceNotification?.payload;

    if (
      lastDataSourceNotification &&
      lastHandledNotificationRef.current !== lastDataSourceNotification
    ) {
      lastHandledNotificationRef.current = lastDataSourceNotification;

      if (
        payload?.AlertType === 11 &&
        payload?.SourceId === sourceId &&
        payload?.Status === 1
      ) {
        const failedState =
          payload?.ResultState !== 1
            ? resultStateEnums.find(
                (rse) => rse?.value === payload?.ResultState
              )?.name ?? "Failed"
            : "";

        setProgressBar({
          showProgress: true,
          inProgress: false,
          failed: failedState,
        });
      } else if (
        payload?.AlertType === 35 &&
        payload?.SourceId === sourceId &&
        (reportId == null ||
          payload?.DataSourceReport?.RefreshSummaryId === reportId)
      ) {
        setProgressBar({
          showProgress: true,
          inProgress: false,
          failed: "",
        });

        setLastDataSourceNotification(null);
        fetchLatestReport();
      }
    }
  }, [
    lastDataSourceNotification,
    sourceId,
    reportId,
    fetchLatestReport,
    setLastDataSourceNotification,
  ]);

  const goToDataSource = () => {
    const targetPath = reportId
      ? dataSourceReport(sourceId, reportId)
      : dataSource(sourceId);

    setLastDataSourceNotification(null);
    setProgressBar({ showProgress: false, inProgress: false, failed: "" });

    if (history.location.pathname === targetPath) {
      // Already on the page, force reload
      window.location.reload();
    } else {
      history.push(targetPath);
    }
  };

  if (!progressBar.showProgress) return null;

  return (
    <ReportStatusContainer>
      <ReportStatus>
        {progressBar.inProgress ? (
          "The Report is currently updating... This may take a few minutes."
        ) : progressBar.failed ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {progressBar.failed}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center" }}>
            The Report has finished updating. If the report does not reload
            automatically,{" "}
            <div
              style={{
                color: "#fefefe",
                fontWeight: "bold",
                cursor: "pointer",
                marginLeft: ".5rem",
              }}
              onClick={goToDataSource}
              data-testid="refreshLink"
            >
              refresh now
            </div>
          </div>
        )}
      </ReportStatus>
    </ReportStatusContainer>
  );
};

export default React.memo(ReportProgressStatus);
